import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Divider, FormHelperText, ListSubheader } from "@mui/material";
import React from "react";

type LabelValuePairType = {
  label: string;
  value: string;
};

type OptionGroupType = {
  label: string;
  options: LabelValuePairType[];
};

type FooterActionType = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
};

type SelectDropdownPropsType = {
  name?: string;
  options?: LabelValuePairType[] | [];
  groups?: OptionGroupType[];
  onChangeCb?: (e: SelectChangeEvent<string>) => void;
  value?: string;
  label?: string;
  size?: any;
  error?: boolean;
  helperText?: string;
  isStarPresent?: boolean;
  displayEmpty?: boolean;
  disabled?: boolean;
  placeholderValue?: string; // if you use this you need displayEmpty true property
  onBlur?: (e: any) => void;
  footerAction?: FooterActionType;
};

const SelectDropdown = ({
  name = "",
  label = "",
  value = "",
  options = [],
  groups,
  isStarPresent = false,
  displayEmpty = false,
  placeholderValue,
  disabled = false,
  onChangeCb = () => {
    /* no-op */
  },
  size = "small",
  error = false,
  helperText,
  onBlur = (e) => {},
  footerAction,
}: SelectDropdownPropsType) => {
  // Render either grouped options (with section headers + dividers) or the
  // legacy flat options list. Grouped rendering is opt-in via the `groups` prop
  // so existing flat-`options` usages are unaffected.
  const renderOptions = () => {
    if (groups && groups.length) {
      const hasGroupOptions = groups.some((group) => group.options.length);
      if (!hasGroupOptions) {
        return (
          <MenuItem value="">
            <em>No data found</em>
          </MenuItem>
        );
      }
      return groups.flatMap((group, groupIndex) => {
        if (!group.options.length) return [];
        const items = [
          <ListSubheader
            key={`__group_header_${group.label}__`}
            sx={{
              lineHeight: "32px",
              color: "#717C7E",
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "uppercase",
            }}
          >
            {group.label}
          </ListSubheader>,
          ...group.options.map((item) => (
            <MenuItem key={item?.value} value={item.value}>
              {item.label}
            </MenuItem>
          )),
        ];
        if (groupIndex > 0) {
          items.unshift(<Divider key={`__group_divider_${group.label}__`} />);
        }
        return items;
      });
    }

    return options.length ? (
      options.map((item) => {
        return (
          <MenuItem key={item?.value} value={item.value}>
            {item.label}
          </MenuItem>
        );
      })
    ) : (
      <MenuItem value="">
        <em>No data found</em>
      </MenuItem>
    );
  };

  return (
    <Box sx={{ width: "100%", minWidth: 120 }}>
      <FormControl fullWidth size={size} error={error}>
        <InputLabel>
          {label} {isStarPresent && <span style={{ color: "#CE0718" }}>*</span>}
        </InputLabel>
        <Select
          disabled={disabled}
          name={name}
          value={value}
          displayEmpty={displayEmpty}
          label={label}
          onChange={onChangeCb}
          onBlur={onBlur}
          sx={{
            color: value === "" ? "#717C7E" : "", // Gray for placeholder, black for selected
            "& .Mui-disabled": {
              backgroundColor: "#F4F4F4", // Set background for disabled state
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300, // Set the maximum height of the dropdown
                overflow: "auto",
              },
            },
          }}
        >
          {placeholderValue && (
            <MenuItem value="" sx={{ color: "#717C7E" }}>
              {placeholderValue}
            </MenuItem>
          )}
          {renderOptions()}
          {footerAction && (
            <MenuItem
              key="__footer_action__"
              disableRipple
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 1,
                backgroundColor: "#fff",
                borderTop: "1px solid #EAEAEA",
                color: "#005596",
                fontWeight: 500,
                "&:hover": { backgroundColor: "#F4F8FB" },
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                footerAction.onClick();
              }}
            >
              {footerAction.icon}
              {footerAction.label}
            </MenuItem>
          )}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export default SelectDropdown;
